import { useState, useEffect, useMemo, useRef } from 'react';
import { Layers, Filter, AlertCircle, TrendingUp, TrendingDown, ExternalLink, Loader2, Settings, RefreshCw, Search, DollarSign, Target, Eye } from 'lucide-react';
import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

// Paramètres par défaut
const DEFAULT_SETTINGS = {
  period: 'LAST_30_DAYS',
  maxPosition: 1.5,
  minSeaCost: 0
};

// Nom de l'outil pour la sauvegarde des paramètres
const TOOL_NAME = 'seaSeoOverlap';

const SeaSeoOverlapTool = ({ token, selectedClientId, updateUrlWithAccount, setCurrentView, setOpenSearchConsoleOnLibraryLoad }) => {
  // États pour les comptes Google Ads
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // États pour les sites GSC
  const [gscConnected, setGscConnected] = useState(false);
  const [gscSites, setGscSites] = useState([]);
  const [selectedGscSite, setSelectedGscSite] = useState('');
  const [loadingGscSites, setLoadingGscSites] = useState(false);

  // États pour les données
  const [overlapsData, setOverlapsData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // États pour les paramètres
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // État pour le tri
  const [sortColumn, setSortColumn] = useState('sea.cost');
  const [sortDirection, setSortDirection] = useState('desc');

  // État pour le modal de détails
  const [selectedOverlap, setSelectedOverlap] = useState(null);

  // Ref pour annuler les requêtes en cours
  const abortControllerRef = useRef(null);

  // Ref pour identifier les requêtes obsolètes
  // S'incrémente à chaque changement de client pour invalider les anciennes requêtes
  const requestIdRef = useRef(0);

  // Charger les comptes au montage et réinitialiser quand le client change
  useEffect(() => {
    if (!token) return;

    // Incrémenter le requestId pour invalider toutes les requêtes en cours
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    console.log(`[SEA-SEO] Client changed, new requestId: ${currentRequestId}`);

    // Annuler toutes les requêtes en cours
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Créer un nouveau controller pour ce cycle
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Réinitialiser tous les états
    setSelectedAccount('');
    setSelectedGscSite('');
    setOverlapsData([]);
    setStats(null);
    setError(null);
    setLoading(false);
    setLoadingAccounts(true);
    setLoadingGscSites(true);
    setSettingsLoaded(false);

    // Charger les comptes et sites GSC avec le signal d'annulation
    const loadData = async () => {
      try {
        // Charger les comptes Google Ads
        const params = selectedClientId ? { clientId: selectedClientId } : {};
        const [accountsRes, gscRes] = await Promise.all([
          axios.get(`${API_URL}/api/library?usage=outils`, {
            params,
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          axios.get(`${API_URL}/api/library?usage=outils`, {
            params,
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          })
        ]);

        // Vérifier si cette requête est encore valide (même requestId)
        if (currentRequestId !== requestIdRef.current) {
          console.log(`[SEA-SEO] Ignoring stale loadData response (requestId: ${currentRequestId}, current: ${requestIdRef.current})`);
          return;
        }

        // Traiter les comptes Google Ads
        if (accountsRes.data.success && accountsRes.data.library) {
          const data = accountsRes.data;
          const googleSheetsItems = data.library?.GOOGLE_SHEETS || [];
          const googleAdsItems = data.library?.GOOGLE_ADS || [];
          const adsItems = [
            ...googleSheetsItems.filter(item => item.type === 'GOOGLE_ADS'),
            ...googleAdsItems.filter(item => item.type === 'GOOGLE_ADS')
          ];
          // Trier les comptes par ordre alphabétique
          const sortedAccounts = [...adsItems].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
          );
          setAccounts(sortedAccounts);
          if (sortedAccounts.length === 1) {
            setSelectedAccount(sortedAccounts[0].id);
            if (updateUrlWithAccount) updateUrlWithAccount(sortedAccounts[0].id);
          }
        }

        // Traiter les sites GSC
        if (gscRes.data.success && gscRes.data.library) {
          const gscItems = gscRes.data.library?.GOOGLE_SEARCH_CONSOLE || [];
          if (gscItems.length === 0) {
            setGscConnected(!!selectedClientId);
            setGscSites([]);
          } else {
            setGscConnected(true);
            const sites = gscItems.map(item => {
              const metadata = typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata;
              return {
                id: item.databaseId || item.libraryId || item.id,
                sourceId: item.sourceId,
                siteUrl: metadata?.siteUrl || item.name,
                name: item.name,
                permissionLevel: metadata?.permissionLevel
              };
            });
            setGscSites(sites);
            if (sites.length === 1) {
              setSelectedGscSite(sites[0].siteUrl);
            }
          }
        } else {
          setGscConnected(false);
          setGscSites([]);
        }
      } catch (e) {
        // Ignorer si requête annulée ou obsolète
        if (axios.isCancel(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
        if (currentRequestId !== requestIdRef.current) return;
        console.error('Erreur chargement:', e);
        setError('Erreur lors du chargement des comptes');
      } finally {
        // Vérifier que cette requête est encore valide avant de mettre à jour loading
        if (currentRequestId === requestIdRef.current) {
          setLoadingAccounts(false);
          setLoadingGscSites(false);
        }
      }
    };

    loadData();

    // Cleanup: annuler les requêtes si le composant se démonte ou si selectedClientId change
    return () => {
      controller.abort();
    };
  }, [token, selectedClientId]);

  // Charger les paramètres depuis l'API quand le compte change
  useEffect(() => {
    const loadSettings = async () => {
      if (!token || !selectedAccount) {
        setSettings(DEFAULT_SETTINGS);
        setSettingsLoaded(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success && response.data.settings) {
          setSettings({ ...DEFAULT_SETTINGS, ...response.data.settings });
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      } catch (e) {
        console.error('Erreur chargement paramètres:', e);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadSettings();
  }, [token, selectedAccount]);

  // Sauvegarder les paramètres dans l'API quand ils changent
  useEffect(() => {
    if (!settingsLoaded || !token || !selectedAccount) return;

    const saveSettings = async () => {
      try {
        await axios.post(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`,
          { settings },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (e) {
        console.error('Erreur sauvegarde paramètres:', e);
      }
    };

    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [settings, settingsLoaded, token, selectedAccount]);

  // Vérifier si les paramètres sont personnalisés
  const hasCustomSettings = useMemo(() => {
    return Object.keys(DEFAULT_SETTINGS).some(key => {
      return String(DEFAULT_SETTINGS[key]) !== String(settings[key]);
    });
  }, [settings]);

  // Analyser les overlaps
  const fetchData = async () => {
    if (!selectedAccount || !selectedGscSite) return;

    // Capturer le requestId actuel pour vérifier si la requête est encore valide
    const currentRequestId = requestIdRef.current;
    console.log(`[SEA-SEO] Starting fetchData with requestId: ${currentRequestId}`);

    // Annuler la requête précédente si elle existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Créer un nouveau controller pour cette requête
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Trouver le site GSC sélectionné pour obtenir son ID
    const selectedGscSiteObj = gscSites.find(s => s.siteUrl === selectedGscSite);

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/api/tools/sea-seo-overlap/analyze`, {
        params: {
          clientId: selectedAccount,
          gscLibraryItemId: selectedGscSiteObj?.id,
          gscSiteUrl: selectedGscSite,
          period: settings.period,
          maxPosition: settings.maxPosition,
          minSeaCost: settings.minSeaCost
        },
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });

      // Vérifier si cette requête est encore valide (même requestId)
      if (currentRequestId !== requestIdRef.current) {
        console.log(`[SEA-SEO] Ignoring stale fetchData response (requestId: ${currentRequestId}, current: ${requestIdRef.current})`);
        return;
      }

      if (response.data.success) {
        setOverlapsData(response.data.data.overlaps || []);
        setStats(response.data.data.stats || null);
      } else {
        setError(response.data.error || 'Erreur lors de l\'analyse');
      }
    } catch (e) {
      // Ignorer les erreurs d'annulation (AbortController) ou requêtes obsolètes
      if (axios.isCancel(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') {
        console.log(`[SEA-SEO] Request cancelled (requestId: ${currentRequestId})`);
        return;
      }
      if (currentRequestId !== requestIdRef.current) {
        console.log(`[SEA-SEO] Ignoring stale error (requestId: ${currentRequestId}, current: ${requestIdRef.current})`);
        return;
      }

      console.error('Erreur analyse:', e);

      // Si erreur de permissions GSC, proposer la reconnexion complète
      if (e.response?.data?.requiresReconnect) {
        const confirmReconnect = window.confirm(
          'Les permissions Google Search Console sont insuffisantes.\n\nVoulez-vous supprimer l\'ancienne connexion et reconnecter GSC avec les bonnes permissions ?'
        );
        if (confirmReconnect) {
          try {
            // D'abord supprimer l'ancienne connexion pour forcer une réautorisation complète
            await axios.delete(`${API_URL}/api/auth/google-search-console`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log('Ancienne connexion GSC supprimée');
          } catch (deleteErr) {
            console.error('Erreur suppression connexion GSC:', deleteErr);
            // Continuer même si la suppression échoue
          }
          // Puis rediriger vers OAuth
          window.location.href = `${API_URL}/api/auth/google-search-console?token=${token}`;
          return;
        }
      }

      setError(e.response?.data?.error || 'Erreur lors de l\'analyse SEA-SEO');
    } finally {
      // Ne mettre à jour loading que si la requête est encore valide
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Lancer l'analyse quand les paramètres changent
  useEffect(() => {
    if (token && selectedAccount && selectedGscSite && settingsLoaded) {
      fetchData();
    }
  }, [token, selectedAccount, selectedGscSite, settingsLoaded]);

  // Connecter GSC
  const handleConnectGsc = () => {
    window.location.href = `${API_URL}/api/auth/google-search-console?token=${token}`;
  };

  // Tri des données
  const sortedData = useMemo(() => {
    if (!overlapsData.length) return [];

    return [...overlapsData].sort((a, b) => {
      let aVal, bVal;

      // Accéder aux valeurs imbriquées
      const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc?.[part], obj);
      };

      aVal = getNestedValue(a, sortColumn);
      bVal = getNestedValue(b, sortColumn);

      // Traitement des valeurs
      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [overlapsData, sortColumn, sortDirection]);

  // Gestionnaire de tri
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Formater les nombres
  const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: decimals }).format(num);
  };

  // Formater les devises
  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
  };

  // Couleur de priorité
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return { bg: '#FEE2E2', text: '#DC2626', label: 'Haute' };
      case 'MEDIUM': return { bg: '#FEF3C7', text: '#D97706', label: 'Moyenne' };
      case 'LOW': return { bg: '#DBEAFE', text: '#2563EB', label: 'Basse' };
      default: return { bg: '#F3F4F6', text: '#6B7280', label: 'Très basse' };
    }
  };

  // Icône de tri
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <span style={{ opacity: 0.3 }}>↕</span>;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Layers size={28} />
          Cannibalisation SEA/SEO
        </h1>
        <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
          Identifiez les termes de recherche payants (SEA) pour lesquels vous êtes déjà en position 1 en organique (SEO)
        </p>
      </div>

      {/* Sélecteurs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
        {/* Compte Google Ads */}
        <div>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
            Compte Google Ads
          </label>
          {!loadingAccounts && accounts.length === 0 && selectedClientId ? (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: '#92400E',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>Aucun compte Google Ads associé</span>
              <button
                onClick={() => window.location.hash = '#sites'}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#D97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                Associer
              </button>
            </div>
          ) : (
            <select
              value={selectedAccount}
              onChange={(e) => {
                setSelectedAccount(e.target.value);
                if (updateUrlWithAccount) updateUrlWithAccount(e.target.value);
              }}
              disabled={loadingAccounts}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '0.875rem',
                backgroundColor: loadingAccounts ? '#F9FAFB' : 'white'
              }}
            >
              <option value="">Sélectionner un compte</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name || account.sourceName}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Site GSC */}
        <div>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
            Site Google Search Console
          </label>
          {!gscConnected ? (
            <button
              onClick={handleConnectGsc}
              style={{
                width: '100%',
                padding: '10px 16px',
                backgroundColor: '#4285F4',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <ExternalLink size={16} />
              Connecter Google Search Console
            </button>
          ) : !loadingGscSites && gscSites.length === 0 && selectedClientId ? (
            <div style={{
              padding: '10px 12px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: '#92400E',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>Aucun site GSC associé</span>
              <button
                onClick={() => {
                  if (setOpenSearchConsoleOnLibraryLoad && setCurrentView) {
                    setOpenSearchConsoleOnLibraryLoad(true);
                    setCurrentView('library');
                  } else {
                    window.location.hash = '#library';
                  }
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#D97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                Associer
              </button>
            </div>
          ) : (
            <select
              value={selectedGscSite}
              onChange={(e) => setSelectedGscSite(e.target.value)}
              disabled={loadingGscSites}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '0.875rem',
                backgroundColor: loadingGscSites ? '#F9FAFB' : 'white'
              }}
            >
              <option value="">Sélectionner un site</option>
              {gscSites.map(site => (
                <option key={site.id} value={site.siteUrl}>
                  {site.siteUrl}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Boutons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '10px 16px',
              backgroundColor: hasCustomSettings ? '#EEF2FF' : '#F3F4F6',
              color: hasCustomSettings ? '#4F46E5' : '#374151',
              border: hasCustomSettings ? '1px solid #C7D2FE' : '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Filter size={16} />
            Filtres
          </button>
          <button
            onClick={fetchData}
            disabled={loading || !selectedAccount || !selectedGscSite}
            style={{
              padding: '10px 16px',
              backgroundColor: '#4F46E5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Analyser
          </button>
        </div>
      </div>

      {/* Panneau de filtres */}
      {showFilters && (
        <div style={{
          backgroundColor: '#F9FAFB',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            {/* Période */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Période
              </label>
              <select
                value={settings.period}
                onChange={(e) => setSettings(prev => ({ ...prev, period: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="LAST_7_DAYS">7 derniers jours</option>
                <option value="LAST_14_DAYS">14 derniers jours</option>
                <option value="LAST_30_DAYS">30 derniers jours</option>
                <option value="LAST_60_DAYS">60 derniers jours</option>
                <option value="LAST_90_DAYS">90 derniers jours</option>
              </select>
            </div>

            {/* Position SEO max */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Position SEO maximale
              </label>
              <select
                value={settings.maxPosition}
                onChange={(e) => setSettings(prev => ({ ...prev, maxPosition: parseFloat(e.target.value) }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="1">Position 1 uniquement</option>
                <option value="1.5">Position 1-1.5</option>
                <option value="2">Position 1-2</option>
                <option value="3">Position 1-3</option>
              </select>
            </div>

            {/* Coût SEA minimum */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Coût SEA minimum
              </label>
              <input
                type="number"
                value={settings.minSeaCost}
                onChange={(e) => setSettings(prev => ({ ...prev, minSeaCost: parseFloat(e.target.value) || 0 }))}
                min="0"
                step="1"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
          </div>

          {/* Réinitialiser les filtres */}
          {hasCustomSettings && (
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button
                onClick={() => setSettings(DEFAULT_SETTINGS)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: 'none',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          color: '#DC2626',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px',
          color: '#6B7280'
        }}>
          <Loader2 size={32} className="animate-spin" style={{ marginRight: '12px' }} />
          Analyse en cours...
        </div>
      )}

      {/* Statistiques */}
      {!loading && stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ backgroundColor: '#EEF2FF', padding: '10px', borderRadius: '10px' }}>
                <Layers size={20} style={{ color: '#4F46E5' }} />
              </div>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Termes en overlap</span>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827' }}>
              {formatNumber(stats.totalOverlaps)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '4px' }}>
              sur {formatNumber(stats.totalSeaTerms)} termes SEA
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ backgroundColor: '#FEF3C7', padding: '10px', borderRadius: '10px' }}>
                <DollarSign size={20} style={{ color: '#D97706' }} />
              </div>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Budget SEA concerné</span>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827' }}>
              {formatCurrency(stats.totalSeaCost)}
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ backgroundColor: '#D1FAE5', padding: '10px', borderRadius: '10px' }}>
                <TrendingDown size={20} style={{ color: '#059669' }} />
              </div>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Économies potentielles</span>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#059669' }}>
              {formatCurrency(stats.totalPotentialSavings)}
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ backgroundColor: '#FEE2E2', padding: '10px', borderRadius: '10px' }}>
                <Target size={20} style={{ color: '#DC2626' }} />
              </div>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Priorité haute</span>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#DC2626' }}>
              {formatNumber(stats.priorityBreakdown?.HIGH || 0)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '4px' }}>
              termes à revoir en priorité
            </div>
          </div>
        </div>
      )}

      {/* Tableau des résultats */}
      {!loading && sortedData.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB' }}>
                  <th
                    onClick={() => handleSort('term')}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Terme de recherche <SortIcon column="term" />
                  </th>
                  <th
                    onClick={() => handleSort('seo.position')}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'right',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Position SEO <SortIcon column="seo.position" />
                  </th>
                  <th
                    onClick={() => handleSort('sea.clicks')}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'right',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Clics SEA <SortIcon column="sea.clicks" />
                  </th>
                  <th
                    onClick={() => handleSort('seo.clicks')}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'right',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Clics SEO <SortIcon column="seo.clicks" />
                  </th>
                  <th
                    onClick={() => handleSort('sea.cost')}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'right',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Coût SEA <SortIcon column="sea.cost" />
                  </th>
                  <th
                    onClick={() => handleSort('sea.conversions')}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'right',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Conv. SEA <SortIcon column="sea.conversions" />
                  </th>
                  <th
                    onClick={() => handleSort('analysis.priority')}
                    style={{
                      padding: '14px 16px',
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Priorité <SortIcon column="analysis.priority" />
                  </th>
                  <th style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((overlap, index) => {
                  const priorityStyle = getPriorityColor(overlap.analysis.priority);
                  return (
                    <tr
                      key={index}
                      style={{
                        borderTop: '1px solid #E5E7EB',
                        backgroundColor: overlap.analysis.priority === 'HIGH' ? '#FEF2F2' : 'white'
                      }}
                    >
                      <td style={{ padding: '14px 16px', fontSize: '0.875rem', color: '#111827', fontWeight: '500' }}>
                        {overlap.term}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontSize: '0.875rem', color: '#059669', fontWeight: '600' }}>
                        {overlap.seo.position.toFixed(1)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontSize: '0.875rem', color: '#374151' }}>
                        {formatNumber(overlap.sea.clicks)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontSize: '0.875rem', color: '#374151' }}>
                        {formatNumber(overlap.seo.clicks)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontSize: '0.875rem', color: '#D97706', fontWeight: '600' }}>
                        {formatCurrency(overlap.sea.cost)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontSize: '0.875rem', color: '#374151' }}>
                        {formatNumber(overlap.sea.conversions, 1)}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: priorityStyle.bg,
                          color: priorityStyle.text
                        }}>
                          {priorityStyle.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => setSelectedOverlap(overlap)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            color: '#374151',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Eye size={14} />
                          Détails
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Message si pas de données */}
      {!loading && !error && sortedData.length === 0 && selectedAccount && selectedGscSite && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#6B7280'
        }}>
          <Layers size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
          <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '8px' }}>
            Aucun overlap détecté
          </p>
          <p style={{ fontSize: '0.875rem' }}>
            Aucun terme de recherche SEA n'est en position 1 en SEO pour cette période.
          </p>
        </div>
      )}

      {/* Modal de détails */}
      {selectedOverlap && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setSelectedOverlap(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
              Détails: {selectedOverlap.term}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* SEA */}
              <div style={{ backgroundColor: '#FEF3C7', borderRadius: '12px', padding: '16px' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#D97706', marginBottom: '12px' }}>
                  SEA (Google Ads)
                </h3>
                <div style={{ display: 'grid', gap: '8px', fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Impressions</span>
                    <span style={{ fontWeight: '500' }}>{formatNumber(selectedOverlap.sea.impressions)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Clics</span>
                    <span style={{ fontWeight: '500' }}>{formatNumber(selectedOverlap.sea.clicks)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>CTR</span>
                    <span style={{ fontWeight: '500' }}>{selectedOverlap.sea.ctr?.toFixed(2) || '0.00'}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Coût</span>
                    <span style={{ fontWeight: '500' }}>{formatCurrency(selectedOverlap.sea.cost)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Conversions</span>
                    <span style={{ fontWeight: '500' }}>{formatNumber(selectedOverlap.sea.conversions, 1)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Valeur conv.</span>
                    <span style={{ fontWeight: '500' }}>{formatCurrency(selectedOverlap.sea.conversionsValue)}</span>
                  </div>
                </div>

                {/* Campagnes SEA */}
                {selectedOverlap.sea.campaigns?.length > 0 && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #FCD34D' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#D97706', marginBottom: '6px' }}>
                      Campagnes ({selectedOverlap.sea.campaigns.length})
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#92400E' }}>
                      {selectedOverlap.sea.campaigns.map((campaign, i) => (
                        <div key={i} style={{ marginBottom: '2px' }}>
                          • {typeof campaign === 'string' ? campaign : campaign.campaignName || campaign}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedOverlap.sea.campaignType && (
                  <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#92400E' }}>
                    Type: {selectedOverlap.sea.campaignType}
                  </div>
                )}
              </div>

              {/* SEO */}
              <div style={{ backgroundColor: '#D1FAE5', borderRadius: '12px', padding: '16px' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#059669', marginBottom: '12px' }}>
                  SEO (Search Console)
                </h3>
                <div style={{ display: 'grid', gap: '8px', fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Impressions</span>
                    <span style={{ fontWeight: '500' }}>{formatNumber(selectedOverlap.seo.impressions)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Clics</span>
                    <span style={{ fontWeight: '500' }}>{formatNumber(selectedOverlap.seo.clicks)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>CTR</span>
                    <span style={{ fontWeight: '500' }}>{(selectedOverlap.seo.ctr * 100).toFixed(2)}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Position</span>
                    <span style={{ fontWeight: '500' }}>{selectedOverlap.seo.position.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Analyse */}
            <div style={{ marginTop: '20px', backgroundColor: '#F3F4F6', borderRadius: '12px', padding: '16px' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                Analyse
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.875rem' }}>
                <div>
                  <span style={{ color: '#6B7280' }}>Part de clics SEA</span>
                  <div style={{ fontWeight: '500', marginTop: '4px' }}>
                    {selectedOverlap.analysis.seaClickShare.toFixed(1)}%
                  </div>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Part de clics SEO</span>
                  <div style={{ fontWeight: '500', marginTop: '4px' }}>
                    {selectedOverlap.analysis.seoClickShare.toFixed(1)}%
                  </div>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Total clics (SEA+SEO)</span>
                  <div style={{ fontWeight: '500', marginTop: '4px' }}>
                    {formatNumber(selectedOverlap.analysis.totalClicks)}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Économie potentielle</span>
                  <div style={{ fontWeight: '500', marginTop: '4px', color: '#059669' }}>
                    {formatCurrency(selectedOverlap.analysis.potentialSavings)}
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setSelectedOverlap(null)}
              style={{
                width: '100%',
                marginTop: '24px',
                padding: '12px',
                backgroundColor: '#4F46E5',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeaSeoOverlapTool;
